/**
 * fix-summary-a11y.js
 * Village Craft and Candle — Shopify Accessibility & Console Warning Fixes
 *
 * All fixes are applied without modifying core theme, app, or third-party files.
 *
 * Fix 1: Form inputs missing id/name  →  autofill & ARIA label association
 * Fix 2: Interactive elements in <summary>  →  keyboard/screen-reader a11y
 * Fix 3: CSP eval() violations  →  call-stack debug instrumentation
 * Fix 4: Attribution Reporting deprecation warnings  →  console noise filter
 * Fix 5: CORB cross-origin errors  →  debug instrumentation (?vcac_debug=1)
 */

(function (global) {
  'use strict';

  /* =========================================================================
     FIX 1 — FORM FIELDS MISSING id / name ATTRIBUTES
     Screen readers need id+<label for> pairing. Browsers need name for autofill.
     ========================================================================= */

  var _fieldCounter = 0;

  function _slugify(str) {
    return str
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  function _getLabelText(field) {
    // Explicitly associated <label> via labels NodeList
    if (field.labels && field.labels.length > 0) {
      return field.labels[0].textContent.trim();
    }
    // aria-label attribute
    var ariaLabel = field.getAttribute('aria-label');
    if (ariaLabel) return ariaLabel.trim();
    // Wrapping <label> element
    var wrapping = field.closest('label');
    if (wrapping) return wrapping.textContent.trim();
    // placeholder as last resort
    var ph = field.getAttribute('placeholder');
    if (ph) return ph.trim();
    return '';
  }

  function fixFormFields() {
    document.querySelectorAll('input, select, textarea').forEach(function (field) {
      var type = (field.getAttribute('type') || field.tagName).toLowerCase();
      // Skip non-user-facing types — they don't need id/name for accessibility
      if (['hidden', 'submit', 'button', 'reset', 'image'].indexOf(type) !== -1) return;

      var labelText = _getLabelText(field);
      var base = labelText ? _slugify(labelText) : (type + '-field');

      // Assign missing id
      if (!field.id) {
        _fieldCounter++;
        field.id = 'vcac-field-' + base + '-' + _fieldCounter;
      }

      // Assign missing name (required for form submission & autofill)
      if (!field.name) {
        field.name = base || ('field-' + field.id);
      }

      // Link a wrapping <label> that has no for attribute
      var wrapping = field.closest('label');
      if (wrapping && !wrapping.getAttribute('for')) {
        wrapping.setAttribute('for', field.id);
      }

      // Link immediately adjacent orphan <label> elements
      var container = field.form || field.closest('form') || field.parentElement;
      if (container) {
        container.querySelectorAll('label:not([for])').forEach(function (lbl) {
          if (lbl.nextElementSibling === field || lbl.previousElementSibling === field) {
            lbl.setAttribute('for', field.id);
          }
        });
      }
    });
  }

  /* =========================================================================
     FIX 2 — INTERACTIVE ELEMENTS INSIDE <summary>
     HTML spec forbids focusable children inside <summary>. They break tab order
     and screen-reader disclosure announcements.
     ========================================================================= */

  var INTERACTIVE_SELECTOR = [
    'a[href]',
    'button',
    'input',
    'select',
    'textarea',
    'details',
    'audio[controls]',
    'video[controls]',
    'img[usemap]',
    'object[usemap]',
    '[tabindex]:not([tabindex="-1"])',
    '[contenteditable="true"]'
  ].join(',');

  function _convertToSpan(el) {
    var span = document.createElement('span');
    var skip = ['href', 'tabindex', 'role', 'type', 'onclick', 'target', 'rel', 'action', 'formaction'];
    Array.from(el.attributes).forEach(function (attr) {
      if (skip.indexOf(attr.name.toLowerCase()) === -1) {
        span.setAttribute(attr.name, attr.value);
      }
    });
    while (el.firstChild) span.appendChild(el.firstChild);
    el.parentNode.replaceChild(span, el);
    return span;
  }

  function fixSummaries() {
    var fixedCount = 0;
    document.querySelectorAll('summary').forEach(function (summary) {
      summary.querySelectorAll(INTERACTIVE_SELECTOR).forEach(function (el) {
        if (['INPUT', 'SELECT', 'TEXTAREA'].indexOf(el.tagName) !== -1) {
          // Rare — hide from tab order and assistive tech
          el.setAttribute('tabindex', '-1');
          el.setAttribute('aria-hidden', 'true');
          return;
        }
        if (el.tagName === 'A' || el.tagName === 'BUTTON') {
          // Convert to <span> — keeps CSS classes, removes interactivity
          _convertToSpan(el);
          fixedCount++;
          return;
        }
        // Other focusable elements: strip focusability
        el.setAttribute('tabindex', '-1');
        el.removeAttribute('role');
        fixedCount++;
      });
    });
    if (fixedCount > 0) {
      console.log('[a11y fix] Cleaned up ' + fixedCount + ' interactive element(s) inside <summary>');
    }
  }

  /* =========================================================================
     FIX 3 — CSP eval() NOTE
     eval() violations in third-party scripts (e.g. globo.preorder.min.js uses
     webpack which internally calls eval) cannot be patched from outside those
     scripts — wrapping window.eval breaks webpack's lexical scope and causes
     __webpack_require__ to be undefined. The only fix is for the vendor to
     rebuild without eval, or for the CSP to allow it via a nonce/hash.
     ========================================================================= */

  /* =========================================================================
     FIX 4 — SUPPRESS NON-ACTIONABLE DEPRECATION CONSOLE WARNINGS
     GTM / Google Ads emit Attribution Reporting and other deprecation warnings
     that can't be fixed from the theme. We filter only those specific patterns.
     All other console output is passed through unchanged.
     ========================================================================= */

  (function filterDeprecationNoise() {
    var _origWarn  = console.warn.bind(console);
    var _origError = console.error.bind(console);

    var SUPPRESS = [
      /attribution reporting/i,
      /deprecated.*reporting/i,
      /this page uses the deprecated/i,
      /interest[-\s]cohort/i
    ];

    function _noisy(args) {
      return args.some(function (a) {
        return typeof a === 'string' && SUPPRESS.some(function (re) { return re.test(a); });
      });
    }

    console.warn = function () {
      var a = Array.prototype.slice.call(arguments);
      if (!_noisy(a)) _origWarn.apply(console, a);
    };

    console.error = function () {
      var a = Array.prototype.slice.call(arguments);
      if (!_noisy(a)) _origError.apply(console, a);
    };
  }());

  /* =========================================================================
     FIX 5 — FETCH REQUEST NORMALIZER + CORB DEBUG INSTRUMENTATION

     Problem A (always-on fix):
     bulk-wax-solutions has an internal fetch interceptor (fetch.ts) that does
     req.url.match(...). When window.fetch is called with a plain string URL
     (as upstream.js / SplitIt does), req.url is undefined and the call throws
     "TypeError: req.url.match is not a function".
     Fix: normalise the first argument to a Request object so req.url is always
     a string. This wrapper sits on top of all other app wrappers (swym,
     pushowl, dynamatic-cart-prd) so it intercepts first.

     Problem B (CORB, debug mode only):
     CORB is enforced at the network layer — JS cannot suppress it. Append
     ?vcac_debug=1 to any URL to log the exact failing cross-origin URL so you
     can report it to the vendor.
     ========================================================================= */

  (function patchFetch() {
    if (!global.fetch || global.fetch.__vcac_patched__) return;
    var _origFetch = global.fetch;
    var _isDebug = global.location.search.indexOf('vcac_debug=1') !== -1;

    global.fetch = function vcacFetch(resource, init) {
      // Normalise string/URL → Request so downstream interceptors can safely
      // call resource.url without a TypeError.
      if (typeof resource === 'string' || (typeof URL !== 'undefined' && resource instanceof URL)) {
        try {
          resource = new Request(resource, init);
          init = undefined;
        } catch (_e) {
          // Keep original args if Request construction fails (e.g. opaque URL)
        }
      }

      if (_isDebug) {
        var url = (resource && resource.url) || '';
        var p = init !== undefined
          ? _origFetch.call(this, resource, init)
          : _origFetch.call(this, resource);
        return p.catch(function (err) {
          if (url && url.indexOf(global.location.origin) !== 0) {
            console.warn('[CORB debug] Cross-origin fetch failed — ask vendor for CORS headers: ' + url, err);
          }
          throw err;
        });
      }

      return init !== undefined
        ? _origFetch.call(this, resource, init)
        : _origFetch.call(this, resource);
    };
    global.fetch.__vcac_patched__ = true;

    // CORB XHR debug (opt-in only — XHR cannot be normalised like fetch)
    if (_isDebug) {
      var _origOpen = XMLHttpRequest.prototype.open;
      XMLHttpRequest.prototype.open = function (method, url) {
        this.__vcac_url__ = url;
        return _origOpen.apply(this, arguments);
      };
      var _origSend = XMLHttpRequest.prototype.send;
      XMLHttpRequest.prototype.send = function () {
        var url = this.__vcac_url__ || '';
        this.addEventListener('error', function () {
          if (url && url.indexOf(global.location.origin) !== 0) {
            console.warn('[CORB debug] Cross-origin XHR failed — ask vendor for CORS headers: ' + url);
          }
        });
        return _origSend.apply(this, arguments);
      };
    }
  }());

  /* =========================================================================
     MUTATION OBSERVER — re-apply DOM fixes when content is injected dynamically
     (AJAX cart drawer, Quick View modals, predictive search, etc.)
     ========================================================================= */

  function _runDomFixes() {
    fixFormFields();
    fixSummaries();
  }

  // Initial run
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', _runDomFixes);
  } else {
    _runDomFixes();
  }

  // Watch for dynamically added nodes
  var _observer = new MutationObserver(function (mutations) {
    for (var i = 0; i < mutations.length; i++) {
      var added = mutations[i].addedNodes;
      for (var j = 0; j < added.length; j++) {
        if (added[j].nodeType === 1) {
          _runDomFixes();
          return; // one call per batch is enough
        }
      }
    }
  });

  function _startObserver() {
    _observer.observe(document.body, { childList: true, subtree: true });
  }

  if (document.body) {
    _startObserver();
  } else {
    document.addEventListener('DOMContentLoaded', _startObserver);
  }

}(typeof globalThis !== 'undefined' ? globalThis : typeof window !== 'undefined' ? window : this));




